import { ApiClient, CurrentLogApi, IAccountSubscriptionStatus, ICruise, ICurrentDayResponse, IDay, ILog, ILogLighting, ILogResponse, ISailChange, IShipStatus, IStatus, ITrip } from "../../Types"
import type { IStopRequest } from "../../Types/Generated/LogbookApp/CurrentLogApi"
import { PreviousDayProps } from "../DayView"

export enum FocusViewConfirmationType {
  cruise = "cruise",
  trip = "trip",
};

export interface FocusViewState {
  cruise: ICruise;
  day: number;
  firstDay: boolean;
  days: IDay[];
  logs: ILog[];
  // In-progress (uncommitted) auto-logs on the current day, keyed by their
  // `metadata.provisionalKey` — the same shape `useDayEvents` keeps for rendering
  // them in the log list, mirrored here because `UserAdvice.update` runs off this
  // state and a pending pause (#1218) is only visible through its marker. They all
  // share id 0, so they can never be folded into `logs`.
  provisionalLogs: Record<string, ILog>;
  statuses: IStatus[];
  subscriptionStatus?: IAccountSubscriptionStatus;
  trip?: ITrip;
}

// The message-catalogue keys the subscription warning can raise. `sevenDays` maps to
// the `7Days` accessor (which is not a valid identifier); every other key matches its
// `m.subscription.<key>` accessor one-for-one.
export type SubscriptionWarningKey =
  | "demoStarted"
  | "demo2Days"
  | "demo1Day"
  | "demoToday"
  | "demoEnded"
  | "sevenDays"
  | "subscriptionExpired";

export const FocusViewData = {
  loadCurrentDay(api: ApiClient): Promise<ICurrentDayResponse> {
    return new CurrentLogApi(api).current.day({ params: {} }).call();
  },
  recordLog(api: ApiClient, status?: IShipStatus): Promise<ILogResponse> {
    return new CurrentLogApi(api).current.log({ params: {}, body: { status } }).call();
  },
  recordEngine(api: ApiClient, engineId: string, on: boolean): Promise<ILogResponse> {
    return new CurrentLogApi(api).current.engine({ params: {}, body: { engine: engineId, on } }).call();
  },
  recordLighting(api: ApiClient, lighting: ILogLighting): Promise<ILogResponse> {
    return new CurrentLogApi(api).current.lighting({ params: {}, body: { lighting } }).call();
  },
  recordSails(api: ApiClient, changes: ISailChange[]): Promise<ILogResponse> {
    return new CurrentLogApi(api).current.sails({ params: {}, body: { sails: changes } }).call();
  },
  recordStop(api: ApiClient, values: IStopRequest): Promise<ILogResponse> {
    return new CurrentLogApi(api).current.stop({ params: {}, body: values }).call();
  },
  // Whether the account is effectively expired.
  //
  // The loaded day state wins only where its status actually settles the question. A
  // trial status does: the server recomputes it from the trial's own day count, so it
  // is fresher than the account row. `cancelled` does NOT -- it means the subscription
  // will not renew, not that access has ended; that is `nextBillingAt`'s call, and only
  // the account carries it. So `cancelled` (and the equally inconclusive `inactive`)
  // defer to the AppView flag, which is `App/IAP.ts`'s `subscriptionExpired` mirroring
  // the server's `Account.hasExpiredSubscription`.
  //
  // Treating any present status as conclusive meant a lapsed account -- cancelled, past
  // its billing date -- fell through every branch of `subscriptionWarningKey` below and
  // rendered no warning at all (issue #1336). Note `inactive` is 0, so the truthiness
  // check this replaced also mis-read it as "no status loaded"; it now takes the
  // deferring branch deliberately rather than by accident.
  accountSubscriptionExpired(subscriptionStatus: IAccountSubscriptionStatus | undefined, appViewExpired: boolean): boolean {
    switch (subscriptionStatus) {
      case IAccountSubscriptionStatus.trialExpired:
        return true;
      case IAccountSubscriptionStatus.trialStarted:
      case IAccountSubscriptionStatus.trial1DayLeft:
      case IAccountSubscriptionStatus.trial2DaysLeft:
      case IAccountSubscriptionStatus.trial3DaysLeft:
      case IAccountSubscriptionStatus.trial4DaysLeft:
      case IAccountSubscriptionStatus.trial5DaysLeft:
      case IAccountSubscriptionStatus.active:
        return false;
      default:
        return appViewExpired;
    }
  },
  // Which subscription warning (if any) to raise for the given account state.
  // `undefined` means no warning. The dismissed-today check stays in the component.
  subscriptionWarningKey(
    accountStatus: IAccountSubscriptionStatus | undefined,
    nextBillingAt: string | undefined,
    now: Date,
    accountSubscriptionExpired: boolean,
  ): SubscriptionWarningKey | undefined {
    if (accountStatus === IAccountSubscriptionStatus.trialStarted) {
      return "demoStarted";
    } else if (accountStatus === IAccountSubscriptionStatus.trial3DaysLeft) {
      return "demo2Days";
    } else if (accountStatus === IAccountSubscriptionStatus.trial2DaysLeft) {
      return "demo1Day";
    } else if (accountStatus === IAccountSubscriptionStatus.trial1DayLeft) {
      return "demoToday";
    } else if (accountStatus === IAccountSubscriptionStatus.trialExpired) {
      return "demoEnded";
    } else if (accountStatus === IAccountSubscriptionStatus.active && nextBillingAt) {
      const daysUntilRenewal = Math.floor((new Date(nextBillingAt).getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      if (daysUntilRenewal <= 7 && daysUntilRenewal >= 0) {
        return "sevenDays";
      }
      return undefined;
    } else if (accountSubscriptionExpired) {
      return "subscriptionExpired";
    }
    return undefined;
  },
  // Yesterday's day record within the loaded state (undefined without a trip).
  dayYesterday(state?: FocusViewState): IDay | undefined {
    if (!state || !state.trip) {
      return undefined;
    }
    return state.days.find((d) => d.day == state.day - 1);
  },
  // Today's day is "active" once it has a real day number and at least one log.
  dayTodayActive(dayToday: IDay | undefined, logs: ILog[]): boolean {
    return dayToday != undefined && dayToday.day > 0 && logs.find((l) => l.dayId == dayToday.id) != undefined;
  },
  // Tomorrow's day record (or the pending day-0 placeholder). Only surfaced once
  // today is active, and only within a trip.
  dayTomorrow(state: FocusViewState | undefined, dayTodayActive: boolean | undefined): IDay | undefined {
    if (!state || !state.trip || !dayTodayActive) {
      return undefined;
    }
    return state.days.find((d) => d.day == state.day + 1 || d.day == 0);
  },
  // Whether the current-day state needs to be (re)loaded from the API. Reload when
  // nothing is loaded yet, or when the calendar day has genuinely advanced past the
  // loaded day. The comparison is done in the loaded trip's fixed navigation timezone
  // (the same timezone the server used to compute `state.day`), so a non-zero timezone
  // no longer makes the two clocks disagree on every render — that disagreement was the
  // endless reload loop (a burst of `GET /api/ship/current/day` requests). When there is
  // no trip yet, fall back to the App's own system timezone rather than a hardcoded UTC:
  // the server (DayApi.current) falls back to `TimeZone.currentTimezoneOffset` (the App
  // instance's local timezone) in that same case, and a UTC/local mismatch there caused
  // the same endless-reload bug (issue #1151).
  // Accepts any structural `{ day, trip? }` (the full `FocusViewState` or the New
  // variant's lighter `FocusFrameData`, which omits logs/statuses) since only the day
  // number and the trip's fixed navigation timezone matter here.
  shouldLoadCurrentDay(state: { day: number; trip?: { timezone: number } } | undefined, now: Date, systemTimezone: number, lastRequestedDay?: number): boolean {
    if (!state) {
      return true;
    }
    const today = FocusViewData.todayDayNumber(now, state.trip?.timezone ?? systemTimezone);
    return state.day !== today && lastRequestedDay !== today;
  },
  // The "day number" for the current calendar day (whole days since the Apple reference
  // date 2001-01-01 UTC), offset by the given timezone (seconds east of UTC). Mirrors the
  // server's `Date.days(timezone:)`. Used to detect when the loaded day is stale.
  todayDayNumber(now: Date, timezone: number): number {
    return Math.floor((now.valueOf() + timezone * 1000 - new Date(Date.UTC(2001, 0, 1)).valueOf()) / (60 * 60 * 24 * 1000));
  },
  // The day record for the current day: the matching day, else the pending day-0
  // placeholder (renumbered to today), else a freshly synthesized blank day carrying
  // the trip's identifiers. Undefined without a loaded trip.
  dayToday(state: FocusViewState | undefined, currentLogTimezone: number | undefined): IDay | undefined {
    if (!state || !state.trip) {
      return undefined;
    }
    const dayToday = state.days.find((d) => d.day == state.day);
    if (dayToday) {
      return dayToday;
    }
    const nextDay = state.days.find((d) => d.day == 0);
    if (nextDay) {
      return { ...nextDay, day: state.day };
    }
    return { id: 0, cruiseId: state.trip.cruiseId, tripId: state.trip.id, accountId: state.trip.accountId, shipId: state.trip.shipId, createdAt: state.trip.createdAt, updatedAt: state.trip.updatedAt, day: state.day, timezone: currentLogTimezone || state.trip.timezone, data: "{}" };
  },
  // The carry-over defaults for the next day: crew/passengers/skipper persist and the
  // new day starts where the previous one ended. Undefined without a current day.
  previousDayDefaults(dayToday: IDay | undefined): PreviousDayProps | undefined {
    if (!dayToday) {
      return undefined;
    }
    return {
      crew: dayToday.crew,
      passengers: dayToday.passengers,
      skipper: dayToday.skipper,
      startLocation: dayToday.endLocation,
    };
  },
};
